import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  MenuItem
} from '@mui/material';
import { SwapHoriz as ConvertIcon, Cancel as CancelIcon } from '@mui/icons-material';
import useLeadStore from '../../stores/leadStore';
import PageHeader from '../../components/ui/PageHeader';
import useForm from '../../hooks/useForm';

const industryOptions = [
  'Luxury',
  'Hospitality',
  'Finance',
  'Technology',
  'E-commerce',
  'Healthcare',
  'Education',
  'Real Estate',
  'Food & Beverage',
  'Automotive',
  'Media & Entertainment',
  'Manufacturing',
  'Retail',
  'Professional Services',
  'Non-Profit',
  'Government',
  'Other'
];

const regionOptions = [
  'Egypt',
  'Saudi Arabia',
  'Qatar',
  'UAE',
  'Kuwait',
  'Bahrain',
  'Oman',
  'Other'
];

const LeadConvert = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Zustand store hooks
  const selectedLead = useLeadStore(state => state.selectedLead);
  const storeLoading = useLeadStore(state => state.loading);
  const fetchLeadById = useLeadStore(state => state.fetchLeadById);
  const clearSelectedLead = useLeadStore(state => state.clearSelectedLead);
  const convertToClient = useLeadStore(state => state.convertToClient);

  const initialValues = {
    name: '',
    industry: '',
    region: 'Egypt',
    subscriptionTier: 'standard',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Egypt'
    }
  };

  const validate = (values) => {
    const errors = {};
    
    if (!values.name) {
      errors.name = 'Client name is required';
    }
    
    if (!values.industry) {
      errors.industry = 'Industry is required';
    }
    
    if (!values.region) {
      errors.region = 'Region is required';
    }
    
    return errors;
  };

  const { 
    formData: values, 
    handleChange, 
    updateFormData: setValues,
    validationErrors: errors
  } = useForm(initialValues);

  useEffect(() => {
    fetchLeadById(id);
    return () => {
      clearSelectedLead();
    };
  }, [id]);

  // Update form values when lead is loaded
  useEffect(() => {
    if (selectedLead) {
      setValues({
        ...initialValues,
        name: selectedLead.company || selectedLead.name,
        industry: selectedLead.industry || ''
      });
    }
  }, [selectedLead]);

  const onSubmit = async (e) => {
    e.preventDefault();
    
    // Run validation
    const validationErrors = validate(values);
    if (Object.keys(validationErrors).length > 0) {
      // Show first error
      const firstError = Object.values(validationErrors)[0];
      console.error('Validation error:', firstError);
      return;
    }
    
    setLoading(true);
    try {
      await convertToClient(id, values);
      navigate('/clients');
    } catch (error) {
      console.error('Failed to convert lead:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddressChange = (field) => (event) => {
    setValues({
      ...values,
      address: {
        ...values.address,
        [field]: event.target.value
      }
    });
  };

  if (storeLoading || !selectedLead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const lead = selectedLead;

  if (lead.convertedToClient) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          This lead has already been converted to a client.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Convert Lead to Client"
        subtitle={`Converting: ${lead.name}`}
      />

      <Paper sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          You are about to convert this lead to a client. The lead record will be 
          marked as "Won" and a new client record will be created.
        </Alert>

        <form onSubmit={onSubmit}>
          <Grid container spacing={3}>
            {/* Client Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Client Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Client Name"
                name="name"
                value={values.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Industry"
                name="industry"
                value={values.industry}
                onChange={handleChange}
                error={!!errors.industry}
                helperText={errors.industry}
                required
              >
                {industryOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Region"
                name="region"
                value={values.region}
                onChange={handleChange}
                error={!!errors.region}
                helperText={errors.region}
                required
              >
                {regionOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Subscription Tier"
                name="subscriptionTier"
                value={values.subscriptionTier}
                onChange={handleChange}
              >
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
                <MenuItem value="enterprise">Enterprise</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </TextField>
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Address (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={values.address.street}
                onChange={handleAddressChange('street')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={values.address.city}
                onChange={handleAddressChange('city')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="State/Province"
                value={values.address.state}
                onChange={handleAddressChange('state')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Zip Code"
                value={values.address.zipCode}
                onChange={handleAddressChange('zipCode')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={values.address.country}
                onChange={handleAddressChange('country')}
              />
            </Grid>

            {/* Form Actions */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate(`/leads/${id}`)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<ConvertIcon />}
                  disabled={loading}
                >
                  {loading ? 'Converting...' : 'Convert to Client'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default LeadConvert;